import { useState } from 'react'
import { Autocomplete, useLoadScript } from '@react-google-maps/api'
import { customerData, CustomerReceiptData, CustomerShippoReceiptData, ShippoRate } from '../../utilities/CustomerData'
import { apiURL } from '../../utilities/URLs'
import { refreshAccessToken } from '../../utilities/extensions'
import { useLocation, useNavigate } from 'react-router-dom'
import CreditCardInput from 'react-credit-card-input'
import { PaymentConfirmation, PaymentDeclined } from '../Payment/PaymentModals'

export function ShippoRates() {
  const navigate = useNavigate()
  const location = useLocation()
  const isPostPaid = location.state.isPostPaid

  const libraries = ['places']

  const MAX_FAILED_ATTEMPTS = 3
  const LOCKOUT_TIME_MS = 60 * 60 * 1000

  const { isLoaded } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries
  })

  const [step, setStep] = useState(0)
  const [autocomplete, setAutocomplete] = useState(null)
  const [errors, setErrors] = useState({})

  const [street, setStreet] = useState('')
  const [city, setCity] = useState('')
  const [state, setState] = useState('')
  const [zip, setZip] = useState('')
  const [fetchingRates, setFetchingRates] = useState(false)

  const [shippoRates, setShippoRates] = useState([])
  const [selectedShippoRate, setSelectedShippoRate] = useState(new ShippoRate())

  const [formData, setFormData] = useState({
    cardNumber: '',
    expiryDate: '',
    cvv: ''
  })

  const [payingRate, setPayingRate] = useState(false)
  const [paymentDeclined, setPaymentDeclined] = useState(false)
  const [paymentError, setPaymentError] = useState('')
  const [paymentCode, setPaymentCode] = useState('')
  const [retriesRemaining, setRetriesRemaining] = useState(3)

  const [confirmPayment, setConfirmPayment] = useState(false)
  const [generatedReceipt, setGeneratedReceipt] = useState(new CustomerReceiptData())

  const handleCardNumberChange = (e) => {
    setFormData({ ...formData, cardNumber: e.target.value })
  }

  const handleCardExpiryChange = (e) => {
    setFormData({ ...formData, expiryDate: e.target.value })
  }

  const handleCardCVCChange = (e) => {
    setFormData({ ...formData, cvv: e.target.value })
  }

  const handleNextStep = () => {
    setStep((prevStep) => prevStep + 1)
  }

  const handleLoad = (autocompleteInstance) => {
    setAutocomplete(autocompleteInstance)
  }

  const handlePlaceChanged = () => {
    const getAddressComponent = (type) => {
      const component = place.address_components.find(comp => comp.types.includes(type))
      return component ? component.long_name : ''
    }

    const place = autocomplete.getPlace()
    if (place) {
      setStreet(`${getAddressComponent('street_number')} ${getAddressComponent('route')}`.trim())
      setCity(getAddressComponent('locality'))
      setState(getAddressComponent('administrative_area_level_1'))
      setZip(getAddressComponent('postal_code'))
    }
  }

  const handleCheckAddress = async () => {
    const newErrors = {}

    if (!street) newErrors.street = 'Street is required'
    if (!city) newErrors.city = 'City is required'
    if (!state) newErrors.state = 'State is required'
    if (!zip) newErrors.zip = 'Zip is required'

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors)
      return
    }

    await handleGetShippoRates()
  }

  const handleGetShippoRates = async () => {
    setFetchingRates(true)

    const form = new FormData()
    form.append('addressInfo', JSON.stringify({
      name: customerData.customerLeadData.leadContact.contactName,
      street: street,
      city: city,
      state: state,
      zip: zip,
      phone: customerData.customerLeadData.leadContact.contactPhone,
      email: customerData.customerLeadData.leadContact.contactEmail,
      isPostPaid: isPostPaid
    }))

    const shippoRatesResponse = await fetch(`${apiURL}/api/getShippoRates`, {
      method: 'POST',
      headers: {
        'Authorization': customerData.accessToken
      },
      body: form
    })
    const shippoRatesData = await shippoRatesResponse.json()
    setFetchingRates(false)
    if (shippoRatesData.response.error) {
      if (shippoRatesData.response.error === 'Failed To Authenticate Token.') {
        const refreshedToken = await refreshAccessToken()
        refreshedToken && refreshedToken.length > 0 ? await handleGetShippoRates() : navigate('/', { state: { expired: true } })
      } else
        console.error(shippoRatesData.response.error)
    } else {
      setShippoRates(shippoRatesData.response.shippoRates)
      setStep(step + 1)
    }
  }

  const handlePayShippoRate = async () => {
    setPayingRate(true)

    const failedAttempts = parseInt(localStorage.getItem('failedAttempts') || '0', 10)
    const lastFailedAttempt = parseInt(localStorage.getItem('lastFailedAttempt') || '0', 10)

    const currentTime = new Date().getTime()

    if (failedAttempts >= MAX_FAILED_ATTEMPTS) {
      if (currentTime - lastFailedAttempt < LOCKOUT_TIME_MS) {
        setPaymentError(`You have reached the maximum number of failed payment attempts. Please try again in ${new Date(LOCKOUT_TIME_MS - (currentTime - lastFailedAttempt)).getMinutes()} minutes.`)
        setRetriesRemaining(0)
        setPaymentDeclined(true)
        setPayingRate(false)
        return
      } else {
        localStorage.removeItem('failedAttempts')
        localStorage.removeItem('lastFailedAttempt')
      }
    }

    const form = new FormData()
    form.append('shippoRate', JSON.stringify({
      customer: customerData.customerLeadData.leadContact,
      custoemrCreditCardInfod: {
        cardNumber: formData.cardNumber,
        cardExpiryDate: formData.expiryDate,
        cardCVV: formData.cvv
      },
      shippoRate: selectedShippoRate
    }))

    const payShippoRateResponse = await fetch(`${apiURL}/api/payShippoRate`, {
      method: 'POST',
      headers: {
        'Authorization': customerData.accessToken
      },
      body: form
    })
    const payShippoRateData = await payShippoRateResponse.json()
    setPayingRate(false)
    if (payShippoRateData.response.error) {
      if (payShippoRateData.response.error === 'Failed To Authenticate Token.') {
        const refreshedToken = await refreshAccessToken()
        refreshedToken && refreshedToken.length > 0 ? await handlePayShippoRate() : navigate('/', { state: { expired: true } })
      } else
        console.error(payShippoRateData.response.error)
    } else {
      if (payShippoRateData.response.paymentError) {
        const currentTime = new Date().getTime()
        let failedAttempts = parseInt(localStorage.getItem('failedAttempts') || '0', 10)
        let lastFailedAttempt = parseInt(localStorage.getItem('lastFailedAttempt') || '0', 10)

        failedAttempts++
        lastFailedAttempt = currentTime

        localStorage.setItem('failedAttempts', failedAttempts)
        localStorage.setItem('lastFailedAttempt', lastFailedAttempt)

        if (failedAttempts >= MAX_FAILED_ATTEMPTS) {
          setPaymentError('You have reached the maximum number of failed payment attempts. Please try again in 60 minutes.')
          setRetriesRemaining(0)
          setPaymentCode('')
          setPaymentDeclined(true)
        } else {
          setFormData({
            cardNumber: '',
            expiryDate: '',
            cvv: ''
          })
          setPaymentError(payShippoRateData.response.paymentError)
          setPaymentCode(payShippoRateData.response.paymentErrorCode)
          setRetriesRemaining(3 - failedAttempts)
          setPaymentDeclined(true)
        }
      } else {
        customerData.customerShippoReceipts.push(new CustomerShippoReceiptData(payShippoRateData.response.shippoReceipt))
        setGeneratedReceipt(new CustomerShippoReceiptData(payShippoRateData.response.shippoReceipt))
        setConfirmPayment(true)
      }
    }
  }

  const handlePaymentConfirmation = () => {
    setConfirmPayment(false)
    setStep(step + 1)
  }

  return (
    <div className='relative flex flex-col justify-center items-center w-full'>
      {(step > 0 && step < 3) && (
        <button onClick={() => setStep(step - 1)} className='absolute left-6 top-6 text-red-700 hover:text-red-900 transition-colors duration-200'>
          <svg xmlns='http://www.w3.org/2000/svg' className='h-6 w-6' fill='none' viewBox='0 0 24 24' stroke='currentColor'>
            <path strokeLinecap='round' strokeLinejoin='round' strokeWidth='2' d='M15 19l-7-7 7-7' />
          </svg>
        </button>
      )}
      <div className={`w-full flex flex-col items-center gap-4 mb-10 ${isPostPaid && 'mt-12'}`}>
        {!isPostPaid && (
          <p onClick={() => navigate('/paymentPortal')} className='ml-auto mr-2 underline text-blue-600 cursor-pointer'>I will handle my own shipping</p>
        )}
        <h2 className='text-3xl font-bold'>Shippo</h2>
        <p className='text-gray-700 max-sm:w-11/12'>Shippo is a premium label service that includes tracking for your documents. An additional fee applies for this service.</p>
      </div>
      {step === 0 && isLoaded ? (
        <div className='w-11/12 lg:w-full flex flex-col items-center justify-center'>
          <div>
            <p className='text-center mb-6'>Please enter your Address you will be shipping your packet from.</p>
            <div className='relative mb-4'>
              <p className='absolute text-sm bg-white left-3 top-[-5px]'>Street</p>
              <Autocomplete onLoad={handleLoad} onPlaceChanged={handlePlaceChanged}>
                <input
                  id='autocomplete'
                  type='text'
                  name='street'
                  placeholder='Street'
                  value={street}
                  onChange={(e) => setStreet(e.target.value)}
                  className={`mt-1 p-3 w-full border rounded-md shadow-sm focus:ring focus:ring-opacity-50 
                ${errors.street ? 'border-red-500' : 'focus:ring-indigo-300'}`}
                />
              </Autocomplete>
              {errors.street && <p className='text-red-500 text-xs mt-1 w-full text-left'>{errors.street}</p>}
            </div>
            <div className='relative mb-4'>
              <p className='absolute text-sm bg-white left-3 top-[-5px]'>City</p>
              <input
                type='text'
                name='city'
                placeholder='City'
                value={city}
                onChange={(e) => setCity(e.target.value)}
                className={`mt-1 p-3 w-full border rounded-md shadow-sm focus:ring focus:ring-opacity-50 
              ${errors.city ? 'border-red-500' : 'focus:ring-indigo-300'}`}
              />
              {errors.city && <p className='text-red-500 text-xs mt-1 w-full text-left'>{errors.city}</p>}
            </div>
            <div className='relative mb-4'>
              <p className='absolute text-sm bg-white left-3 top-[-5px]'>State</p>
              <input
                type='text'
                name='state'
                placeholder='State'
                value={state}
                onChange={(e) => setState(e.target.value)}
                className={`mt-1 p-3 w-full border rounded-md shadow-sm focus:ring focus:ring-opacity-50 
              ${errors.state ? 'border-red-500' : 'focus:ring-indigo-300'}`}
              />
              {errors.state && <p className='text-red-500 text-xs mt-1 w-full text-left'>{errors.state}</p>}
            </div>
            <div className='relative mb-4'>
              <p className='absolute text-sm bg-white left-3 top-[-5px]'>Zip</p>
              <input
                type='text'
                name='zip'
                placeholder='Zip'
                value={zip}
                onChange={(e) => setZip(e.target.value)}
                className={`mt-1 p-3 w-full border rounded-md shadow-sm focus:ring focus:ring-opacity-50 
              ${errors.zip ? 'border-red-500' : 'focus:ring-indigo-300'}`}
              />
              {errors.zip && <p className='text-red-500 text-xs mt-1 w-full text-left'>{errors.zip}</p>}
            </div>
            <div className='flex w-full justify-center'>
              <button
                onClick={handleCheckAddress}
                className='px-8 py-2 mt-4 bg-primaryColor hover:bg-primaryDarkColor transition duration-200 text-white rounded focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-opacity-50 max-sm:mb-4'>
                Get Rates
              </button>
            </div>
            {fetchingRates && (
              <div className='flex flex-row items-center justify-center gap-2 -mb-5'>
                <div className='loader ease-linear rounded-full border-[6px] border-t-[6px]  h-8 w-8 mt-4 mb-4'></div>
                <p>Getting Rates...</p>
              </div>
            )}
          </div>
        </div>
      ) : step === 1 ? (
        <div className='w-3/4 h-full flex flex-col items-center mb-10'>
          <p className='font-bold text-2xl'>Available Rates</p>
          <p>Please view below the available rates you can choose from.</p>
          <div className='w-full grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-4'>
            {shippoRates.map((_shippoRate, index) => {
              const shippoRate = new ShippoRate(_shippoRate)
              return (
                <div onClick={() => setSelectedShippoRate(shippoRate)} key={index} className={`cursor-pointer border rounded-lg p-4 shadow-md flex flex-col items-center transition ${selectedShippoRate.objectId === shippoRate.objectId ? 'bg-primaryColor' : 'bg-gray-100 hover:bg-gray-200'}`}>
                  {shippoRate.providerImage75 && (
                    <img src={shippoRate.providerImage200} alt={`${shippoRate.provider} logo`} className='w-12 h-12 mb-4' />
                  )}
                  <p className='text-lg font-bold'>{shippoRate.provider}</p>
                  <p className='text-gray-700'>
                    {shippoRate.currency} {shippoRate.amount}
                  </p>
                  <p className='text-sm text-gray-600 mt-2'>
                    {shippoRate.estimatedDays ? `${shippoRate.estimatedDays} estimated days` : 'No estimate available'}
                  </p>
                  <p className='text-sm text-gray-500'>{shippoRate.durationTerms}</p>
                </div>
              )
            })}
          </div>
          <div className='flex w-full justify-center'>
            <button
              disabled={!selectedShippoRate.objectId}
              onClick={() => setStep(step + 1)}
              className={`px-8 py-2 mt-6 text-white rounded  focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-opacity-50 ${selectedShippoRate.objectId ? 'bg-primaryColor hover:bg-primaryDarkColor transition duration-200' : 'bg-gray-400 cursor-not-allowed'}`}>
              Next
            </button>
          </div>
        </div>
      ) : step === 2 ? (
        <div className='w-3/4 h-fit flex flex-col mb-10 justify-center items-center gap-4 font-mont'>
          <div className='border p-6 rounded-md bg-gray-50 w-1/2'>
            <p className='text-lg font-semibold mb-2'>Selected Rate:</p>
            <div className='w-full border rounded-lg p-4 shadow-md flex flex-col items-center bg-gray-100'>
              {selectedShippoRate.providerImage200 && (
                <img src={selectedShippoRate.providerImage200} alt={`${selectedShippoRate.provider} logo`} className='w-12 h-12 mb-4' />
              )}
              <p className='text-lg font-bold'>{selectedShippoRate.provider}</p>
              <p className='text-gray-700'>
                {selectedShippoRate.currency} {(Number(selectedShippoRate.amount) + (Number(selectedShippoRate.amount) * 0.2)).toFixed(2)}
              </p>
              <p className='text-sm text-gray-600 mt-2'>
                {selectedShippoRate.estimatedDays ? `${selectedShippoRate.estimatedDays} estimated days` : 'No estimate available'}
              </p>
              <p className='text-sm text-gray-500'>{selectedShippoRate.durationTerms}</p>
            </div>
          </div>
          {Number(selectedShippoRate.amount) > 0 ? (
            <div className='flex flex-col'>
              <div className='mb-4 flex flex-col items-center'>
                <label htmlFor='cardNumber' className='text-sm font-medium text-gray-700 text-start w-full'>Card Number</label>
                <CreditCardInput
                  cardNumberInputProps={{
                    value: formData.cardNumber,
                    onChange: handleCardNumberChange,
                  }}
                  cardExpiryInputProps={{
                    value: formData.expiryDate,
                    onChange: handleCardExpiryChange,
                  }}
                  cardCVCInputProps={{
                    value: formData.cvv,
                    onChange: handleCardCVCChange,
                  }}
                  fieldClassName='border border-gray-300 rounded-md'
                />
                <div className='flex gap-4'>
                  {errors.cardNumber && <p className='text-red-500 text-xs mt-1'>{errors.cardNumber}</p>}
                  {errors.expiryDate && <p className='text-red-500 text-xs mt-1'>{errors.expiryDate}</p>}
                  {errors.cvv && <p className='text-red-500 text-xs mt-1'>{errors.cvv}</p>}
                </div>
              </div>
              <div className='mb-4'>
                <button
                  type='submit'
                  onClick={handlePayShippoRate}
                  className={`w-full text-white p-2 rounded-md mt-2 ${payingRate ? 'bg-gray-400 cursor-not-allowed' : 'bg-primaryColor hover:bg-primaryDarkColor transition duration-200'}`}
                  disabled={payingRate}
                >
                  {payingRate ? 'Processing...' : 'Submit Payment'}
                </button>
              </div>
            </div>
          ) : (
            <button
              type='submit'
              onClick={handlePayShippoRate}
              className={`lg:w-1/3 text-white p-2 rounded-md ${payingRate ? 'bg-gray-400 cursor-not-allowed' : 'bg-primaryColor hover:bg-primaryDarkColor transition duration-200'}`}
              disabled={payingRate}
            >
              {payingRate ? 'Generating Label...' : 'Generate Label'}
            </button>
          )}
        </div>
      ) : step === 3 ? (
        <div className='flex flex-col items-center'>
          <p class='text-lg font-medium text-gray-800'>Your label has been created successfully. Once you download your documents, you will see the label attached at the end of your packet.</p>
          <p class='mt-4 text-gray-700'>You may also view your tracking, along with status, and the label, in your document center on the dashboard at any time.</p>
          <div className='flex w-full justify-center'>
            <button
              onClick={() => navigate(isPostPaid ? '/addPaymentMethod' : '/paymentPortal')}
              className='px-8 py-2 mt-6 text-white rounded  focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-opacity-50 max-sm:mb-4 bg-primaryColor hover:bg-primaryDarkColor transition duration-200'>
              Ok
            </button>
          </div>
        </div>
      ) : (
        <div className='flex flex-row items-center justify-center gap-2'>
          <div className='loader ease-linear rounded-full border-[6px] border-t-[6px]  h-8 w-8 mt-4 mb-4'></div>
          <p>Loading...</p>
        </div>
      )}
      <PaymentDeclined
        isOpen={paymentDeclined}
        setIsOpen={setPaymentDeclined}
        paymentError={paymentError}
        paymentCode={paymentCode}
        retriesRemaining={retriesRemaining}
      />
      <PaymentConfirmation
        isOpen={confirmPayment}
        onClose={handlePaymentConfirmation}
        _customerReceipt={generatedReceipt}
        isShippo={true}
      />
    </div>
  )
}
